import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShare } from "../hooks/useShare";
import useFocusTrap from "../hooks/useFocusTrap";
import { formatPrice } from "../utils/formatPrice";
import { trackPixel } from "../utils/analytics";
import { getOptimizedImageUrl, getImageSrcSet } from "../utils/imageUrl";
import "./ItemDetailModal.css";

function ItemDetailModal({
  item,
  isOpen,
  onClose,
  onAddToCart,
  getTranslatedText,
  restaurantId,
  tableNumber,
  mode = "dine_in",
}) {
  const { t } = useTranslation();
  const { shareProduct } = useShare();
  const dialogRef = useFocusTrap(isOpen);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      setSelectedModifiers({});
      setQuantity(1);
      setNotes("");
      setShowToast(false);
      trackPixel("ViewContent", {
        content_name: item.name,
        content_ids: [item.itemId],
        content_type: "product",
        value: item.price,
        currency: "ARS",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.itemId]);

  // Lock body scroll when modal is open (iOS-safe)
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleShare = async () => {
    const result = await shareProduct(item, restaurantId, tableNumber, mode);
    if (result.success && result.method === "clipboard") {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    }
  };

  const isValid = useMemo(() => {
    if (!item?.modifierGroups) return true;
    return item.modifierGroups.every((group) => {
      if (!group.required) return true;
      const selected = selectedModifiers[group.groupId] || [];
      return selected.length >= group.minSelect;
    });
  }, [item, selectedModifiers]);

  const hasPromoPrice = item?.promoPrice != null && item?.promoPrice < item?.price;
  const effectiveBasePrice = hasPromoPrice ? item.promoPrice : (item?.price || 0);

  const totalPrice = useMemo(() => {
    let price = effectiveBasePrice;
    if (item?.modifierGroups) {
      item.modifierGroups.forEach((group) => {
        const selected = selectedModifiers[group.groupId] || [];
        selected.forEach((optionId) => {
          const option = group.options.find((o) => o.id === optionId);
          if (option) price += option.price || 0;
        });
      });
    }
    return price * quantity;
  }, [item, selectedModifiers, quantity, effectiveBasePrice]);

  const handleModifierChange = (groupId, optionId, isMultiple) => {
    setSelectedModifiers((prev) => {
      const current = prev[groupId] || [];
      if (isMultiple) {
        if (current.includes(optionId)) {
          return {
            ...prev,
            [groupId]: current.filter((id) => id !== optionId),
          };
        } else {
          const group = item.modifierGroups.find((g) => g.groupId === groupId);
          if (group && current.length >= group.maxSelect) {
            return { ...prev, [groupId]: [...current.slice(1), optionId] };
          }
          return { ...prev, [groupId]: [...current, optionId] };
        }
      } else {
        return { ...prev, [groupId]: [optionId] };
      }
    });
  };

  const handleAddToCart = () => {
    if (!isValid) return;
    const modifiersForCart = [];
    if (item.modifierGroups) {
      item.modifierGroups.forEach((group) => {
        const selected = selectedModifiers[group.groupId] || [];
        selected.forEach((optionId) => {
          const option = group.options.find((o) => o.id === optionId);
          if (option) {
            modifiersForCart.push({
              groupId: group.groupId,
              groupName: group.name,
              optionId: option.id,
              optionName: option.name,
              price: option.price || 0,
            });
          }
        });
      });
    }
    onAddToCart({
      ...item,
      quantity,
      notes,
      selectedModifiers: modifiersForCart,
      unitPrice: effectiveBasePrice,
      totalModifiersPrice: modifiersForCart.reduce(
        (sum, m) => sum + m.price,
        0,
      ),
    });
    onClose();
  };

  if (!isOpen || !item) return null;

  const itemName = getTranslatedText
    ? getTranslatedText(item, "name")
    : item.name;
  const itemDescription = getTranslatedText
    ? getTranslatedText(item, "description")
    : item.description;

  return (
    <div
      className="item-modal-wrapper"
      role="dialog"
      aria-modal="true"
      aria-labelledby="item-modal-title"
      ref={dialogRef}
    >
      <div className="item-modal-backdrop" onClick={onClose} />
      <div className="item-modal">
        <button
          className="item-modal-share-btn"
          onClick={handleShare}
          aria-label={t("share.share")}
        >
          <span className="material-icons" aria-hidden="true">share</span>
        </button>
        <button className="item-modal-close" onClick={onClose}>
          <span className="material-icons" aria-hidden="true">close</span>
        </button>

        {/* Toast for clipboard feedback */}
        {showToast && (
          <div className="item-modal-toast">{t("share.linkCopied")}</div>
        )}

        {item.imageUrl && (
          <div className="item-modal-image">
            <img
              src={getOptimizedImageUrl(item.imageUrl, { width: 480 })}
              srcSet={getImageSrcSet(item.imageUrl, 480)}
              sizes="(max-width: 480px) 100vw, 480px"
              alt={itemName}
              decoding="async"
            />
          </div>
        )}

        <div className="item-modal-content">
          <div className="item-modal-header">
            <h2 id="item-modal-title">{itemName}</h2>
            <div className="item-modal-price-container">
              {hasPromoPrice && (
                <span className="item-modal-price item-modal-price--original">${formatPrice(item.price)}</span>
              )}
              <span className={`item-modal-price${hasPromoPrice ? ' item-modal-price--promo' : ''}`}>
                ${formatPrice(hasPromoPrice ? item.promoPrice : item.price)}
              </span>
            </div>
          </div>

          {itemDescription && (
            <p className="item-modal-description">{itemDescription}</p>
          )}

          {item.allergens && item.allergens.length > 0 && (
            <div className="item-modal-allergens">
              <h3 className="item-modal-allergens-title">
                <span className="material-icons" aria-hidden="true">warning_amber</span>
                {t("item.allergens")}
              </h3>
              <div className="item-modal-allergens-list">
                {item.allergens.map((allergen, idx) => (
                  <span key={idx} className="item-modal-allergen-tag">
                    {allergen}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.modifierGroups && item.modifierGroups.length > 0 && (
            <div className="item-modal-modifiers">
              {item.modifierGroups.map((group) => (
                <ModifierGroup
                  key={group.groupId}
                  group={group}
                  selectedOptions={selectedModifiers[group.groupId] || []}
                  onChange={(optionId) =>
                    handleModifierChange(
                      group.groupId,
                      optionId,
                      group.maxSelect > 1,
                    )
                  }
                  t={t}
                />
              ))}
            </div>
          )}

          <div className="item-modal-notes">
            <label>{t("item.notes") || "Notas especiales"}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                t("item.notesPlaceholder") || "Ej: Sin cebolla, bien cocido..."
              }
              rows={2}
            />
          </div>

          <div className="item-modal-footer">
            <div className="item-modal-quantity">
              <button
                className="qty-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <span className="material-icons" aria-hidden="true">remove</span>
              </button>
              <span className="qty-value">{quantity}</span>
              <button
                className="qty-btn"
                onClick={() => setQuantity(quantity + 1)}
              >
                <span className="material-icons" aria-hidden="true">add</span>
              </button>
            </div>

            <button
              className={`item-modal-add-btn ${!isValid ? "disabled" : ""}`}
              onClick={handleAddToCart}
              disabled={!isValid}
            >
              <span>{t("item.addToCart") || "Agregar"}</span>
              <span className="add-btn-price">${formatPrice(totalPrice)}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModifierGroup({ group, selectedOptions, onChange, t }) {
  const isMultiple = group.maxSelect > 1;
  const isRequired = group.required;

  return (
    <div className="modifier-group">
      <div className="modifier-group-header">
        <h3>{group.name}</h3>
        <span
          className={`modifier-badge ${isRequired ? "required" : "optional"}`}
        >
          {isRequired ? t("item.required") : t("item.optional")}
          {isMultiple && ` (máx ${group.maxSelect})`}
        </span>
      </div>
      <div className="modifier-options">
        {group.options.map((option) => {
          const isSelected = selectedOptions.includes(option.id);
          const isDisabled = option.available === false;
          return (
            <label
              key={option.id}
              className={`modifier-option ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
            >
              <input
                type={isMultiple ? "checkbox" : "radio"}
                name={group.groupId}
                checked={isSelected}
                onChange={() => !isDisabled && onChange(option.id)}
                disabled={isDisabled}
              />
              <span className="modifier-option-check">
                <span className="material-icons" aria-hidden="true">
                  {isSelected
                    ? isMultiple
                      ? "check_box"
                      : "radio_button_checked"
                    : isMultiple
                      ? "check_box_outline_blank"
                      : "radio_button_unchecked"}
                </span>
              </span>
              <span className="modifier-option-name">{option.name}</span>
              {option.price > 0 && (
                <span className="modifier-option-price">
                  +${formatPrice(option.price)}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default ItemDetailModal;
